import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, Validators} from '@angular/forms';

import {AlertService} from '../../service/alert.service';
import {AuthenticationService} from '../../service/authentication.service';
import {EnvironmentService} from '../../service/environment.service';
import {ConstantsUtil} from '../../util/constantsUtil';
import {DropdownItem} from '../../interface/data/DropdownItem';
import {AddressDetail} from '../../interface/model/address-detail';
import {WizardStepForm} from '../../interface/form/wizard-step-form';

/**
 * The business address details form shared by the signup wizard and the eCommerce KYC verification
 * wizard. Emits the captured address details to the hosting wizard step.
 */
@Component({
    selector: 'app-address-form',
    templateUrl: './address-form.component.html',
    standalone: false
})
export class AddressFormComponent extends WizardStepForm<AddressDetail> implements OnInit, OnChanges
{
    @Input()
    prefilledAddressDetail: AddressDetail;

    /** Driven by the hosting wizard: true while a submission is in flight, to disable the submit button. */
    @Input()
    submitting = false;

    @Input()
    submitLabel = 'Submit';

    @Output()
    previousPage: EventEmitter<boolean>;

    @Output()
    addressDetail: EventEmitter<AddressDetail>;

    readonly provinceList: DropdownItem[] = ConstantsUtil.PROVINCE_LIST.map(province => ({value: province}));

    constructor(
        protected router: Router,
        protected formBuilder: FormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
        this.previousPage = new EventEmitter();
        this.addressDetail = new EventEmitter<AddressDetail>();
    }

    get formControls()
    {
        return this.form.controls;
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        this.patchPrefilledDataToForm(this.prefilledAddressDetail);
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            address1: ['', Validators.required],
            address2: [''],
            city: ['', Validators.required],
            province: ['', Validators.required],
            postalCode: ['', Validators.required],
            billingEmailAddress: ['', [Validators.required, Validators.email]],
        });

        this.patchPrefilledDataToForm(this.prefilledAddressDetail);
        this.applyReadOnlyState();
    }

    /**
     * Emits the captured address details for the hosting wizard to act on. In read-only "View
     * submission" mode nothing is emitted, so the disabled form cannot re-trigger submission.
     */
    submit(): void
    {
        if (!this.readOnly)
        {
            this.addressDetail.emit(this.getAddressDetailFromForm());
        }
    }

    /**
     * Lets the hosting wizard know the user wants to go back to the previous step.
     */
    emitGoToPrevious(): void
    {
        this.previousPage.emit(true);
    }

    /**
     * Builds the output model from the form values.
     */
    private getAddressDetailFromForm(): AddressDetail
    {
        return {
            address1: this.formControls.address1.value,
            address2: this.formControls.address2.value,
            city: this.formControls.city.value,
            province: this.formControls.province.value,
            postalCode: this.formControls.postalCode.value,
            billingEmailAddress: this.formControls.billingEmailAddress.value,
        };
    }
}
